import config from "react-global-configuration";
import { AuthAsyncActions, clearAuth } from "common/reducers/auth.async.actions";
import { store } from "common/store";
import { AnyObject, NumberKeysOf } from "common/types";
import { authService } from "common/services/Auth.service";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { userAclCheck } from "common/selectors/user.selectors";
import { trackNrEvent, noticeError } from "common/helpers/NewRelic";
import { stripBasePath, withBasePath } from "common/helpers/Redirect";

type FormParams = AnyObject | FormData;
type UrlOptionsHandler = (url: string, params?: AnyObject) => string;
type FetchWithRetry = (
  url: string,
  params?: FormParams,
  options?: IFetcherOptions,
  recovered?: boolean,
) => Promise<Response>;

interface IFetcher {
  baseUrl(): string;
  delete<T>(url: string, params?: AnyObject, options?: IFetcherOptions): Promise<T>;
  fetch(url: string, params?: FormParams, options?: IFetcherOptions, recovered?: boolean): Promise<Response>;
  get<T>(url: string, params?: AnyObject, options?: IFetcherOptions): Promise<T>;
  inRecovery(): boolean;
  joinUrls(url: string, ...args: string[]): string;
  json<T>(response: Response): Promise<T>;
  patch<T>(url: string, params?: AnyObject, options?: IFetcherOptions): Promise<T>;
  post<T>(url: string, params?: FormParams, options?: IFetcherOptions): Promise<T>;
  standardStatus(response: Response): Response | Promise<never>;
  urlOptions(url: string, params?: AnyObject): string;
  credentials(url: string, options?: IFetcherOptions): IFetcherOptions;
}

export interface IGlobalRedirectOptions {
  useGlobalAuthRedirect?: boolean;
  useGlobalNotFoundRedirect?: boolean;
}

export interface IFetcherOptions extends IGlobalRedirectOptions, RequestInit {
  formData?: boolean;
}

interface IRecoveryStatus {
  inRecovery: boolean;
  recoveryPromise: Promise<any> | null;
}

const createFetcher = (baseUrl?: string): IFetcher => {
  const resolvedBaseUrl: string = baseUrl || config.get("BASE_URL") || "http://localhost:8001";
  const urlOptions = createUrlOptions(resolvedBaseUrl);

  const status: IRecoveryStatus = { inRecovery: false, recoveryPromise: null };
  const fetcher = fetchData(urlOptions, status);
  const jsonFunction = json;

  return {
    baseUrl: () => resolvedBaseUrl,
    delete: delete_(fetcher, jsonFunction),
    fetch: fetcher,
    get: get(fetcher, jsonFunction),
    inRecovery: () => status.inRecovery || status.recoveryPromise !== null,
    joinUrls: joinUrls(urlOptions),
    json: jsonFunction,
    patch: patch(fetcher, jsonFunction),
    post: post(fetcher, jsonFunction),
    standardStatus: standardStatus,
    urlOptions: urlOptions,
    credentials,
  };
};

const createAnalyticsFetcher = (baseUrl?: string): IFetcher => {
  const resolvedBaseUrl: string = baseUrl || config.get("BASE_URL") || "http://localhost:8001";
  const urlOptions = createUrlOptions(resolvedBaseUrl);

  const status: IRecoveryStatus = { inRecovery: false, recoveryPromise: null };
  const fetcher = fetchData(urlOptions, status);
  const jsonFunction = json;

  return {
    baseUrl: () => resolvedBaseUrl,
    delete: delete_(fetcher, jsonFunction),
    fetch: fetcher,
    get: get(fetcher, analyticsJson),
    inRecovery: () => status.inRecovery || status.recoveryPromise !== null,
    joinUrls: joinUrls(urlOptions),
    json: jsonFunction,
    patch: patch(fetcher, jsonFunction),
    post: post(fetcher, jsonFunction),
    standardStatus: standardStatus,
    urlOptions: urlOptions,
    credentials,
  };
};

const createUrlOptions = (baseUrl: string): UrlOptionsHandler => {
  return (url: string, params: AnyObject = {}): string => {
    const urlIsRelative = url.indexOf("http") !== 0;
    if (!urlIsRelative) {
      if (process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test") {
        console.error("Warning: Absolute URL provided. Consider using a relative path instead.");
      }
    }
    const joined = new URL(url, baseUrl);

    if (Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      for (const k in params) {
        const v = params[k];
        if (Array.isArray(v)) {
          v.forEach((q) => {
            searchParams.append(k, q);
          });
        } else {
          searchParams.append(k, v);
        }
      }
      joined.search = searchParams.toString();
    }

    return joined.toString();
  };
};

const credentials = (url: string, options?: IFetcherOptions): IFetcherOptions => {
  options = options || {};

  if (token()) {
    options.headers = (options.headers || {}) as Record<string, string>;
    const authHeader = url.startsWith(config.get("STORAGE_URL")) ? "occ-authorization" : "Authorization";
    options.headers[authHeader] = "Bearer " + token();
  }

  if (authService.getAccessUid()) {
    options.headers = (options.headers || {}) as Record<string, string>;
    options.headers["x-rsf-auth"] = authService.getAccessUid();
  }

  return options;
};

const delete_ = (fetchWithRetry: FetchWithRetry, jsonFn: <T>(response: Response) => Promise<T>) => {
  return <T>(url: string, params: AnyObject = {}, options: IFetcherOptions = {}): Promise<T> => {
    options = options || {};
    options.method = "DELETE";
    return fetchWithRetry(url, params, options).then(jsonFn<T>);
  };
};

const fetchData = (urlOpts: UrlOptionsHandler, status: IRecoveryStatus) => {
  return async (
    url: string,
    params?: FormParams,
    options?: IFetcherOptions,
    recovered?: boolean,
  ): Promise<Response> => {
    if (status.inRecovery && status.recoveryPromise) {
      return status.recoveryPromise.then(() => {
        return fetchData(urlOpts, status)(url, params, options, true);
      });
    }

    options = headers(credentials(url, options));

    if (options && options.method && (options.method === "POST" || options.method === "PATCH")) {
      if (options.formData) {
        options.body = params as FormData;
        options.headers = (options.headers || {}) as Record<string, string>;
        options.headers["Accept"] = "application/json";
        delete options.headers["Content-Type"];
        delete options.formData;
      } else {
        options.body = JSON.stringify(params);
      }
      url = urlOpts(url);
    } else {
      url = urlOpts(url, params);
    }

    return fetch(url, options).then((response) =>
      retryFailure(urlOpts, status)(response, url, params, options, recovered),
    );
  };
};

const get = (fetchWithRetry: FetchWithRetry, jsonFn: <T>(response: Response) => Promise<T>) => {
  return <T>(url: string, params: AnyObject = {}, options: IFetcherOptions = {}): Promise<T> => {
    return fetchWithRetry(url, params, options).then(jsonFn<T>);
  };
};

const headers = (options: IFetcherOptions): IFetcherOptions => {
  options = options || {};
  options.headers = (options.headers || {}) as Record<string, string>;
  options.headers["X-Requested-With"] = "XMLHttpRequest";

  if (options.method && (options.method === "PATCH" || options.method === "POST")) {
    options.headers["Content-Type"] = "application/json";
  }

  return options;
};

const joinUrls = (urlOpts: UrlOptionsHandler) => {
  return (url: string, ...args: string[]): string => {
    let joined = urlOpts(url);
    if (joined.slice(-1) !== "/") {
      joined = `${joined}/`;
    }

    args.forEach((arg, idx) => {
      joined = new URL(arg, joined).toString();

      if (joined.slice(-1) !== "/" && idx !== args.length - 1) {
        joined = `${joined}/`;
      }
    });
    return joined;
  };
};

const json = <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    return response.json();
  }

  return response.text() as Promise<T>;
};

export const analyticsJson = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") !== -1) {
    const analyticsPagingParameters: { [key: string]: NumberKeysOf<IMetaPaginationData> } = {
      "page-number": "current_page",
      total: "total_count",
      "total-pages": "total_pages",
    };
    const metaPaginationData = {} as IMetaPaginationData;
    response.headers.forEach((value, key) => {
      if (Object.keys(analyticsPagingParameters).includes(key)) {
        const typedKey = analyticsPagingParameters[key];
        metaPaginationData[typedKey] = Number(value);
      }
    });

    const hasMetaPaginationData = Object.values(metaPaginationData).some((value) => value !== undefined);

    if (hasMetaPaginationData) {
      const json = await response.json();
      return { ...json, meta: { pagination: metaPaginationData } };
    } else {
      return response.json();
    }
  }

  return response.text() as Promise<T>;
};

const patch = (fetchWithRetry: FetchWithRetry, jsonFn: <T>(response: Response) => Promise<T>) => {
  return <T>(url: string, params: AnyObject = {}, options: IFetcherOptions = {}): Promise<T> => {
    options = options || {};
    options.method = "PATCH";
    return fetchWithRetry(url, params, options).then(jsonFn<T>);
  };
};

const post = (fetchWithRetry: FetchWithRetry, jsonFn: <T>(response: Response) => Promise<T>) => {
  return <T>(url: string, params: FormParams = {}, options: IFetcherOptions = {}): Promise<T> => {
    options = options || {};
    options.method = "POST";
    return fetchWithRetry(url, params, options).then(jsonFn<T>);
  };
};

const refreshToken = (): string => store.getState().getIn(["auth", "refreshToken"]) as string;

const redirectToErrorPage = (errorPath: string, redirectTo?: string) => {
  if (stripBasePath(window.location.pathname) === errorPath) {
    return;
  }

  const params = redirectTo ? "?redirectTo=" + encodeURIComponent(redirectTo) : "";
  window.location.assign(withBasePath(errorPath) + params);
};

const retryFailure = (urlOpts: UrlOptionsHandler, status: IRecoveryStatus) => {
  return (response: Response, url: string, params?: FormParams, options?: IFetcherOptions, recovered?: boolean) => {
    if (response.ok) {
      return response;
    }

    if (response.status === 404 && options?.useGlobalNotFoundRedirect !== false) {
      redirectToErrorPage("/404");
      return standardStatus(response);
    }

    if (response.status === 403 && options?.useGlobalAuthRedirect !== false) {
      redirectToErrorPage("/403", stripBasePath(window.location.pathname) + window.location.search);
      return standardStatus(response);
    }

    if (response.status === 419 && !recovered) {
      if (!status.recoveryPromise) {
        status.inRecovery = false;
        status.recoveryPromise = store.dispatch(AuthAsyncActions.refreshToken(refreshToken()));
      }

      return status.recoveryPromise
        .then(() => {
          status.recoveryPromise = null;
          return fetchData(urlOpts, status)(url, params, options, true);
        })
        .catch((e) => {
          noticeError(e, { source: "reactive" });
          trackNrEvent("AuthForcedLogout", { source: "reactive" });
          status.recoveryPromise = null;
          store.dispatch(AuthAsyncActions.logout()).catch(() => {
            clearAuth(store.dispatch);
          });
          return standardStatus(response);
        });
    }

    return standardStatus(response);
  };
};

const standardStatus = (response: Response) => {
  if (response.ok) {
    return response;
  }

  const contentType = response.headers.get("content-type");
  const contentIsJson = contentType && contentType.indexOf("application/json") !== -1;
  const promise = contentIsJson ? response.json() : response.text();

  return promise.then((value: any) => {
    return Promise.reject({ status: response.status, statusText: response.statusText, data: value });
  });
};

export const token = (): string => store.getState().getIn(["auth", "token"]) as string;

export const pricelistFetcher = (): IFetcher => {
  const state = store.getState();

  const hasAccess =
    userAclCheck(state, { resource: "flags", permission: "pl_split_main" }) ||
    userAclCheck(state, { resource: "flags", permission: "pl_split_split" });

  return hasAccess ? _pricelistFetcher : fetcher;
};

export const fetcher = createFetcher();
export const analyticsFetcher = createAnalyticsFetcher(config.get("RSF_ANALYTICS_URL"));
const _pricelistFetcher = createFetcher(config.get("PRICELIST_API_URL"));
(window as any).rsfFetcher = fetcher;
